import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { ForgotPassword } from './components/ForgotPassword';
import { ProtectedRoute } from './components/ProtectedRoute';
import { InstagramSettings as InstagramSettingsComponent } from './components/InstagramSettings';
import { MessageTemplates } from './components/MessageTemplates';
import { BusinessInformation as BusinessInformationComponent } from './components/BusinessInformation';
import { Analytics } from './components/Analytics';
import { Updates } from './components/Updates';
import { AccountDetails } from './components/AccountDetails';
import { User, BusinessInformation, InstagramSettings, MessageTemplate, AnalyticsData, Update } from './types';
import { Dashboard } from './components/Dashboard';

export const App: React.FC = () => {
  console.log('App component rendering');
  
  // Add useEffect to log when component mounts
  useEffect(() => {
    console.log('App component mounted');
  }, []);

  const [instagramSettings, setInstagramSettings] = useState<InstagramSettings>({
    autoReply: false,
    replyDelay: 0,
    maxRepliesPerDay: 0
  });

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [businessInfo, setBusinessInfo] = useState<BusinessInformation>({
    businessType: 'b2b',
    industry: '',
    targetAudience: '',
    keywords: [],
    competitorAccounts: []
  });
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalMessages: 0,
    totalResponses: 0,
    responseRate: 0,
    dailyStats: []
  });
  const [updates, setUpdates] = useState<Update[]>([]);
  const [user, setUser] = useState<User>({
    id: '',
    name: '',
    email: '',
    plan: 'basic',
    createdAt: new Date().toISOString()
  });

  const handleInstagramSettingsUpdate = async (settings: InstagramSettings): Promise<void> => {
    try {
      // In a real application, you would make an API call here to save the settings
      console.log('Updating Instagram settings:', settings);
      setInstagramSettings(settings);
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating Instagram settings:', error);
      return Promise.reject(error);
    }
  };

  const handleTemplateUpdate = async (template: MessageTemplate): Promise<void> => {
    setTemplates(prev => [...prev, template]);
  };

  const handleBusinessInfoUpdate = async (info: BusinessInformation): Promise<void> => {
    setBusinessInfo(info);
  };

  const handleAnalyticsUpdate = async (data: AnalyticsData): Promise<void> => {
    setAnalyticsData(data);
  };

  const handleUpdateMarkAsRead = async (updateId: string): Promise<void> => {
    setUpdates(prev => prev.map(update => 
      update.id === updateId ? { ...update, isRead: true } : update
    ));
  };

  const handleUserUpdate = async (userData: Partial<User>): Promise<void> => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const handleAnalyticsRefresh = async (): Promise<void> => {
    // TODO: Implement analytics refresh logic
    return Promise.resolve();
  };

  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard
                    instagramSettings={instagramSettings}
                    templates={templates}
                    businessInfo={businessInfo}
                    analyticsData={analyticsData}
                    updates={updates}
                    user={user}
                    onInstagramSettingsUpdate={handleInstagramSettingsUpdate}
                    onTemplateUpdate={handleTemplateUpdate}
                    onBusinessInfoUpdate={handleBusinessInfoUpdate}
                    onAnalyticsUpdate={handleAnalyticsUpdate}
                    onUpdateMarkAsRead={handleUpdateMarkAsRead}
                    onUserUpdate={handleUserUpdate}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/instagram-settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <InstagramSettingsComponent
                    settings={instagramSettings}
                    onUpdate={handleInstagramSettingsUpdate}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/message-templates"
            element={
              <ProtectedRoute>
                <Layout>
                  <MessageTemplates
                    templates={templates}
                    onUpdate={handleTemplateUpdate}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/business-information"
            element={
              <ProtectedRoute>
                <Layout>
                  <BusinessInformationComponent
                    businessInfo={businessInfo}
                    onUpdate={handleBusinessInfoUpdate}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/analytics"
            element={
              <ProtectedRoute>
                <Layout>
                  <Analytics
                    data={analyticsData}
                    onRefresh={handleAnalyticsRefresh}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/updates"
            element={
              <ProtectedRoute>
                <Layout>
                  <Updates
                    updates={updates}
                    onMarkAsRead={handleUpdateMarkAsRead}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/account"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountDetails
                    user={user}
                    onUpdate={handleUserUpdate}
                  />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};
